import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/api/api';
import { CustomerFeedbackForm } from '@/models/customersTypes';
import ActionLink from '@/components/UI/ActionLink';
import FormField, { EMAIL_INVALID_TEXT, EMAIL_REGEX, SEND_FAILED_TEXT, SHORT_MAX_LENGTH, SubmitButton, TEXT_MAX_LENGTH, inputClass } from '@/components/Forms/FormField';
import StarRating from '@/components/Forms/StarRating';
import StoreSelect, { storeLabel, useStores } from '@/components/Forms/StoreSelect';
import { track } from '@/helpers/analytics';

type RatingKey = 'cleanliness' | 'tidiness' | 'availability' | 'assortment';

const RATINGS: { key: RatingKey; label: string }[] = [
    { key: 'cleanliness', label: 'Sauberkeit' },
    { key: 'tidiness', label: 'Ordnung' },
    { key: 'availability', label: 'Warenverfügbarkeit' },
    { key: 'assortment', label: 'Sortiment' },
];

const EMPTY_FORM: CustomerFeedbackForm = {
    store: '',
    cleanliness: null,
    tidiness: null,
    availability: null,
    assortment: null,
    message: '',
    email: '',
};

function validate(form: CustomerFeedbackForm) {
    const errors: Record<string, string> = {};
    if (!form.store.trim()) errors.store = 'Bitte wähle einen Standort aus.';
    const rated = RATINGS.some(({ key }) => form[key] !== null);
    if (!rated && !form.message.trim()) errors.message = 'Bitte vergib Sterne oder schreibe kurz, worum es geht.';
    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) errors.email = EMAIL_INVALID_TEXT;
    return errors;
}

const FeedbackForm: React.FC = () => {
    const { stores, failed } = useStores();
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState<CustomerFeedbackForm>(EMPTY_FORM);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

    const preselectedId = Number(searchParams.get('markt'));
    useEffect(() => {
        const preselected = stores.find((store) => store.id === preselectedId);
        if (preselected) setForm((prev) => ({ ...prev, store: preselected.name }));
    }, [stores, preselectedId]);

    const update = <K extends keyof CustomerFeedbackForm>(key: K, value: CustomerFeedbackForm[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationErrors = validate(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        setStatus('sending');
        try {
            await api.interests.sendFeedback(form);
            track('feedback-sent', {
                store: storeLabel(form.store),
                rated: RATINGS.some(({ key }) => form[key] !== null),
                has_message: form.message.trim().length > 0,
            });
            setStatus('sent');
            setForm(EMPTY_FORM);
        } catch {
            track('form-error', { form: 'feedback' });
            setStatus('error');
        }
    };

    if (status === 'sent') {
        return (
            <div className="rounded-2xl bg-white p-6 shadow-md" role="status">
                <h3 className="mb-2 text-2xl font-bold">Danke für dein Feedback.</h3>
                <p>Deine Nachricht ist bei uns eingegangen. Sie hilft uns, deinen Yobsti weiterzuentwickeln.</p>
                <div className="mt-5">
                    <ActionLink to="/standorte" variant="solid">Zurück zu meinem Standort</ActionLink>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} noValidate className="space-y-6 rounded-2xl bg-white p-6 shadow-md" data-testid="feedback-form">
            <FormField label="Welchen Yobsti meinst du?" htmlFor="feedback-store" required error={errors.store}>
                <StoreSelect
                    id="feedback-store"
                    value={form.store}
                    stores={stores}
                    failed={failed}
                    onChange={(store) => update('store', store)}
                />
            </FormField>

            <fieldset>
                <legend className="mb-2 font-semibold">Wie war dein Einkauf?</legend>
                <div className="divide-y divide-gray-100">
                    {RATINGS.map(({ key, label }) => (
                        <div key={key} className="py-1.5">
                            <StarRating label={label} value={form[key]} onChange={(value) => update(key, value)} />
                        </div>
                    ))}
                </div>
            </fieldset>

            <FormField label="Was möchtest du uns mitteilen?" htmlFor="feedback-message" error={errors.message}>
                <textarea
                    id="feedback-message"
                    rows={4}
                    value={form.message}
                    placeholder="Was ist dir aufgefallen? Welches Produkt wünschst du dir?"
                    onChange={(e) => update('message', e.target.value)}
                    maxLength={TEXT_MAX_LENGTH}
                    className={inputClass}
                />
            </FormField>

            <FormField label="Möchtest du eine Rückmeldung? E-Mail-Adresse (optional)" htmlFor="feedback-email" error={errors.email}>
                <input
                    id="feedback-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    maxLength={SHORT_MAX_LENGTH}
                    className={inputClass}
                />
            </FormField>

            <p className="text-sm text-gray-600">
                Wir verwenden deine Angaben nur, um dein Feedback zu bearbeiten. Mehr dazu in unserer{' '}
                <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
            </p>

            {status === 'error' && <p className="text-red-600" role="alert">{SEND_FAILED_TEXT}</p>}

            <SubmitButton sending={status === 'sending'}>Feedback absenden</SubmitButton>
        </form>
    );
};

export default FeedbackForm;
